import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import KioskModal from './KioskModal';
import { generateImageQrCode } from '../services/generateImageQrCode';

function createUploadId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function UploadQrModal({ open, onClose, onUploaded }) {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    let socket = null;
    const uploadId = createUploadId();

    setQrCodeUrl('');
    setError('');
    setIsLoading(true);

    (async () => {
      const res = await generateImageQrCode(uploadId);
      if (cancelled) return;
      if (!res.success || !res.data?.qrImageUrl) {
        setError(res.error || 'Failed to generate QR code');
        setIsLoading(false);
        return;
      }
      setQrCodeUrl(res.data.qrImageUrl);
      setIsLoading(false);

      const baseUrl = String(process.env.REACT_APP_BASE_URL_WS || '').replace(/\/$/, '');
      socket = io(`${baseUrl}/ws/qr-code`, {
        query: { secretKey: process.env.REACT_APP_IMAGE_QR_CODE_SECRET_KEY || '' },
        transports: ['websocket'],
      });
      socket.on('connect', () => {
        socket.emit('registerUser', { userId: uploadId });
      });
      socket.on(uploadId, (data) => {
        const imageUrl = data?.url || data?.image_url || data?.imageUrl;
        if (imageUrl) onUploadedRef.current?.(imageUrl);
      });
      socket.on('connect_error', () => {
        setError('Connection error. Please try again.');
      });
    })();

    return () => {
      cancelled = true;
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
      }
    };
  }, [open]);

  return (
    <KioskModal show={open} onHide={onClose} labelledBy="upload-qr-title">
        <button
          type="button"
          className="uploadQr__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>
        <h2 id="upload-qr-title" className="uploadQr__title">
          Upload your image
        </h2>
        <p className="uploadQr__desc">
          Scan to using your phone to upload your Image.
          <br />
          You will be redirected to MOSIDA Website upload
        </p>
        <div className="uploadQr__code">
          {isLoading ? <div className="uploadQr__shimmer" /> : null}
          {!isLoading && qrCodeUrl ? <img src={qrCodeUrl} alt="Upload QR code" /> : null}
          {!isLoading && !qrCodeUrl ? (
            <p className="uploadQr__error">{error || 'Failed to load QR code'}</p>
          ) : null}
        </div>
        {error && qrCodeUrl ? <p className="uploadQr__error">{error}</p> : null}
    </KioskModal>
  );
}

export default UploadQrModal;
